'use client';

import ResultLikeButton from '@/components/ResultLikeButton';

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Award, BarChart3, Check, Copy, HelpCircle, Trophy, X } from 'lucide-react';
import { auth } from '@/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import DailyPhaseTimer from '@/components/daily/DailyPhaseTimer';
import FitText from '@/components/FitText';
import HelpTooltipTrigger from '@/components/HelpTooltipTrigger';
import PlayerStatsTrigger from '@/components/PlayerStatsTrigger';
import {
  getDailyMedalPoints,
  getDailyMedalWeights,
  getDailyPlacementMonthlyPoints,
  MIN_FULLY_RATED_SUBMISSIONS,
  MIN_RATED_SUBMISSIONS,
  DAILY_RANKING_RULE_CHANGE_CYCLE_ID,
} from '@/lib/dailyCycle';
import SiteFooter from '@/components/SiteFooter';
import { shareWithAndroidNativeFallback } from '@/lib/nativeShare';
import { buildDailyAcroShareMessage } from '@/utils/shareMessages';

const LOCK_POLL_INTERVAL_MS = 5000;
const JOINT_WINNER_ROTATION_INTERVAL_MS = 10000;
const JOINT_WINNER_FADE_DURATION_MS = 1000;

const getPageFromUrl = () => {
  if (typeof window === 'undefined') return 1;
  const params = new URLSearchParams(window.location.search);
  const parsed = Number.parseInt(params.get('page') || '1', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getCycleFromUrl = () => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return String(params.get('cycleId') || '').trim();
};

const formatVoteTotal = (row, cycleId = '') => {
  const medalPoints = Number(row?.medalPoints);
  if (Number.isFinite(medalPoints)) return medalPoints;
  const goldVotes = Number(row?.goldVotes || 0);
  const silverVotes = Number(row?.silverVotes || 0);
  const bronzeVotes = Number(row?.bronzeVotes || 0);
  return getDailyMedalPoints({ cycleId, goldVotes, silverVotes, bronzeVotes });
};

const normalizeAwarderUsernames = (value) => (
  Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : []
);

const formatPercentileLabel = (tier) => {
  switch (String(tier || '').toLowerCase()) {
    case 'top10':
      return 'Top 10%';
    case 'top25':
      return 'Top 25%';
    case 'top50':
      return 'Top 50%';
    default:
      return 'Other';
  }
};

const toOrdinal = (day) => {
  const value = Number(day) || 0;
  if (value % 100 >= 11 && value % 100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
};

const formatCycleDateLabel = (cycleId) => {
  const raw = String(cycleId || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || 'Daily Snapshot';
  const parsed = new Date(`${raw}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return raw;
  const weekday = parsed.toLocaleDateString('en-GB', {
    weekday: 'short',
    timeZone: 'UTC',
  }).toUpperCase();
  const month = parsed.toLocaleDateString('en-GB', {
    month: 'long',
    timeZone: 'UTC',
  }).toUpperCase();
  const day = toOrdinal(parsed.getUTCDate()).toUpperCase();
  return `${weekday} ${day} ${month}`;
};

const getViewerVoteTone = (submissionId, voteSelection) => {
  const normalizedSubmissionId = String(submissionId || '').trim();
  if (!normalizedSubmissionId || !voteSelection) return null;
  if (normalizedSubmissionId === String(voteSelection?.goldSubmissionId || '').trim()) return 'gold';
  if (normalizedSubmissionId === String(voteSelection?.silverSubmissionId || '').trim()) return 'silver';
  if (normalizedSubmissionId === String(voteSelection?.bronzeSubmissionId || '').trim()) return 'bronze';
  return null;
};

const getRowSubmissionId = (row) => (
  String(
    row?.submissionId
    || row?.id
    || row?.submission?.id
    || row?.answerId
    || ''
  ).trim()
);

const formatCycleDateParts = (cycleId) => {
  const raw = String(cycleId || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return {
      label: formatCycleDateLabel(cycleId),
      weekday: '',
      day: '',
      month: '',
      monthKey: '',
    };
  }
  const [, year, month, day] = match;
  const parsed = new Date(`${raw}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return {
      label: formatCycleDateLabel(cycleId),
      weekday: '',
      day: '',
      month: '',
      monthKey: `${year}-${month}`,
    };
  }
  return {
    label: formatCycleDateLabel(cycleId),
    weekday: parsed.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' }).toUpperCase(),
    day: toOrdinal(Number(day)).toUpperCase(),
    month: parsed.toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' }).toUpperCase(),
    monthKey: `${year}-${month}`,
  };
};

const compareCycleIdsAsc = (a, b) => String(a || '').localeCompare(String(b || ''));

const formatMonthLeaderboardLabel = (monthKey) => {
  const raw = String(monthKey || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})$/);
  if (!match) return 'Monthly Leaderboard';
  const [, year, month] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  const monthLabel = date.toLocaleDateString('en-GB', {
    month: 'long',
    timeZone: 'UTC',
  });
  return `${monthLabel} Leaderboard`;
};

const toRomanNumeral = (value) => {
  const number = Number(value);
  if (!Number.isInteger(number) || number <= 0) return '';
  const numerals = [
    ['M', 1000],
    ['CM', 900],
    ['D', 500],
    ['CD', 400],
    ['C', 100],
    ['XC', 90],
    ['L', 50],
    ['XL', 40],
    ['X', 10],
    ['IX', 9],
    ['V', 5],
    ['IV', 4],
    ['I', 1],
  ];
  let remaining = number;
  let result = '';
  numerals.forEach(([symbol, amount]) => {
    while (remaining >= amount) {
      result += symbol;
      remaining -= amount;
    }
  });
  return result;
};

export default function DailyResultsPageClient({
  initialCycleId = '',
  initialCycleHistory = [],
  initialViewerUid = '',
  initialPage = 1,
  initialPayload = null,
  initialLocked = false,
  initialLockSeconds = null,
  initialLockFinalizing = false,
  previewMode = false,
}) {
  const [cycleId, setCycleId] = useState(() => String(initialCycleId || '').trim());
  const [cycleHistory, setCycleHistory] = useState(() => (Array.isArray(initialCycleHistory) ? initialCycleHistory : []));
  const [currentUserUid, setCurrentUserUid] = useState(() => String(initialViewerUid || ''));
  const [page, setPage] = useState(() => Number(initialPage || 1));
  const [loading, setLoading] = useState(() => !initialPayload && !initialLocked);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(initialPayload);
  const [locked, setLocked] = useState(initialLocked);
  const [lockSeconds, setLockSeconds] = useState(initialLockSeconds);
  const [lockFinalizing, setLockFinalizing] = useState(initialLockFinalizing);
  const [copiedSubmissionId, setCopiedSubmissionId] = useState('');
  const [activeTopWinnerIndex, setActiveTopWinnerIndex] = useState(0);
  const [isTopWinnerFading, setIsTopWinnerFading] = useState(false);
  const skippedInitialResultsFetchRef = useRef(Boolean(initialPayload || initialLocked));
  const skippedInitialAuthRefreshRef = useRef(Boolean(initialPayload || initialLocked));
  const payloadRef = useRef(initialPayload);
  const lockedRef = useRef(initialLocked);
  const topWinnerRotationKeyRef = useRef('');

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  useEffect(() => {
    if (previewMode) return;
    if (initialCycleHistory.length > 0) return;
    fetch('/api/leaderboards/daily?history=1')
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const historyItems = Array.isArray(data?.history) ? data.history : [];
        setCycleHistory(historyItems);
      })
      .catch(() => {});
  }, [initialCycleHistory.length, previewMode]);

  const getHeaders = useCallback(async () => {
    const current = auth?.currentUser;
    if (!current) return {};
    const token = await current.getIdToken();
    return token ? { authorization: `Bearer ${token}` } : {};
  }, []);

  const refreshLockState = useCallback(async () => {
    try {
      const response = await fetch('/api/daily/public-live');
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      if (data?.phase !== 'calculating') {
        setLocked(false);
        setLockFinalizing(false);
        setLockSeconds(null);
        return;
      }
      setLocked(true);
      setLockFinalizing(data?.finalizing === true);
      const nextSeconds = Number(data?.countdowns?.secondsToResultsReveal);
      setLockSeconds(Number.isFinite(nextSeconds) ? Math.max(0, Math.ceil(nextSeconds)) : null);
    } catch {
      // Best effort poll while locked.
    }
  }, []);

  const fetchResults = useCallback(async (nextCycleId, nextPage) => {
    if (!nextCycleId) return;
    const shouldShowLoadingState = !payloadRef.current && !lockedRef.current;
    if (shouldShowLoadingState) {
      setLoading(true);
    }
    setError('');
    try {
      const headers = await getHeaders();
      const query = new URLSearchParams({
        cycleId: nextCycleId,
        page: String(nextPage || 1),
      });
      const response = await fetch(`/api/daily/results?${query.toString()}`, {
        cache: 'no-store',
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 423) {
        setLocked(true);
        setLockFinalizing(data?.finalizing === true);
        const nextSeconds = Number(data?.secondsToResultsReveal);
        setLockSeconds(Number.isFinite(nextSeconds) ? Math.max(0, Math.ceil(nextSeconds)) : null);
        if (!payloadRef.current) {
          setPayload(null);
        }
        setError('');
        return;
      }
      if (!response.ok) throw new Error(data?.error || `Failed (${response.status})`);
            if (data && data.ok !== false) {
        setPayload(data);
      }
      setPage(data.page || 1);
      setLocked(false);
      setLockFinalizing(false);
      setLockSeconds(null);
    } catch (nextError) {
      setError(nextError?.message || 'Failed to load results');
    } finally {
      if (shouldShowLoadingState) {
        setLoading(false);
      }
    }
  }, [getHeaders]);

  useEffect(() => {
    if (previewMode) return;
    if (!cycleId) return;
    if (skippedInitialResultsFetchRef.current) {
      skippedInitialResultsFetchRef.current = false;
      return;
    }
    fetchResults(cycleId, page);
  }, [cycleId, page, fetchResults, previewMode]);

  useEffect(() => {
    if (previewMode) return undefined;
    if (!auth) return undefined;
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      const nextUid = String(nextUser?.uid || '');
      setCurrentUserUid(nextUid);
      if (skippedInitialAuthRefreshRef.current && nextUid === String(initialViewerUid || '')) {
        skippedInitialAuthRefreshRef.current = false;
        return;
      }
      skippedInitialAuthRefreshRef.current = false;
    });
    return () => unsubscribe();
  }, [initialViewerUid, previewMode]);

  useEffect(() => {
    if (previewMode) return;
    if (!cycleId) return;
    if (skippedInitialAuthRefreshRef.current) return;
    fetchResults(cycleId, page);
  }, [currentUserUid, cycleId, page, fetchResults, previewMode]);

  useEffect(() => {
    if (!locked) return undefined;
    refreshLockState();
    const pollTimer = setInterval(() => {
      refreshLockState();
    }, LOCK_POLL_INTERVAL_MS);
    return () => clearInterval(pollTimer);
  }, [locked, refreshLockState]);

  useEffect(() => {
    if (!locked || !Number.isFinite(lockSeconds)) return undefined;
    if (lockSeconds <= 0) return undefined;
    const tick = setInterval(() => {
      setLockSeconds((prev) => {
        if (!Number.isFinite(prev)) return prev;
        return Math.max(0, prev - 1);
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [locked, lockSeconds]);

  useEffect(() => {
    if (!locked || !cycleId) return;
    if (Number(lockSeconds) > 0) return;
    const timer = setTimeout(() => {
      fetchResults(cycleId, page);
    }, 1200);
    return () => clearTimeout(timer);
  }, [locked, lockSeconds, cycleId, page, fetchResults]);

  const payloadMatchesCycle = String(payload?.cycleId || '').trim() === cycleId;
  const displayPayload = payloadMatchesCycle ? payload : null;
  const rows = useMemo(() => (Array.isArray(displayPayload?.rows) ? displayPayload.rows : []), [displayPayload?.rows]);
  const usesHalfScoreNonVoterRule = String(displayPayload?.nonVoterRankingRule || '').trim() === 'half_score';
  const cyclePrompt = displayPayload?.prompt || null;
  const payloadChampions = Array.isArray(displayPayload?.champions) ? displayPayload.champions : [];
  const payloadWinnerPreview = displayPayload?.winnerPreview || null;
  const temporarilyHidden = displayPayload?.temporarilyHidden === true;
  const hiddenMessage = String(displayPayload?.hiddenMessage || 'This Daily Acro is temporarily unavailable. Please check back later.').trim();
  const promptCategory = String(cyclePrompt?.category || '').trim();
  const promptLetters = String(cyclePrompt?.letters || '').replace(/[^A-Z]/g, '');
  const handleShareResult = useCallback(async (row) => {
    const uid = String(row?.uid || '').trim();
    const answerText = String(row?.answerText || '').trim();
    const rank = Number(row?.rank);
    if (!uid || !answerText || !Number.isFinite(rank)) return;
    const shareText = buildDailyAcroShareMessage({
      placement: toOrdinal(rank),
      prompt: promptCategory,
      acronym: promptLetters,
      answer: answerText,
    });
    const shareResult = await shareWithAndroidNativeFallback({
      title: 'Daily Acro Result',
      text: shareText,
      clipboardText: shareText,
    }).catch(() => ({ ok: false, method: 'unavailable', cancelled: false }));
    if (!shareResult?.ok || shareResult.method !== 'clipboard') return;
    const submissionId = getRowSubmissionId(row);
    setCopiedSubmissionId(submissionId);
    setTimeout(() => {
      setCopiedSubmissionId((current) => (current === submissionId ? '' : current));
    }, 1500);
  }, [promptCategory, promptLetters]);
  const duplicateRankSet = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      const rank = Number(row?.rank);
      if (!Number.isFinite(rank)) return;
      counts.set(rank, (counts.get(rank) || 0) + 1);
    });
    return new Set(Array.from(counts.entries()).filter(([, count]) => count > 1).map(([rank]) => rank));
  }, [rows]);
  const totalPages = Number(displayPayload?.totalPages) || 1;
  const totalSubmissionCount = Math.max(Number(displayPayload?.totalRows || 0), rows.length);
  const hideMonthlyColumn = totalSubmissionCount > 0 && totalSubmissionCount < MIN_RATED_SUBMISSIONS;
  const lowEntryFootnote = useMemo(() => {
    if (totalSubmissionCount <= 0) return '';
    if (totalSubmissionCount < MIN_RATED_SUBMISSIONS) {
      return `This Daily Acro had only ${totalSubmissionCount} ${totalSubmissionCount === 1 ? 'entry' : 'entries'}. Dailies with 1 or 2 entries do not count toward monthly standings and do not award XP.`;
    }
    if (totalSubmissionCount < MIN_FULLY_RATED_SUBMISSIONS) {
      return `This Daily Acro had ${totalSubmissionCount} entries. Dailies with 3 or 4 entries award half monthly points and XP, rounded up. Voter bonus points only apply from ${MIN_FULLY_RATED_SUBMISSIONS} entries.`;
    }
    return '';
  }, [totalSubmissionCount]);
  const viewerRank = displayPayload?.viewerRank || null;
  const viewerVoteSelection = displayPayload?.viewerVoteSelection || null;
  const cycleDateLabel = formatCycleDateLabel(cycleId);
  const cycleDateParts = formatCycleDateParts(cycleId);
  const monthLeaderboardHref = cycleDateParts.monthKey
    ? `/leaderboards/monthly?monthKey=${encodeURIComponent(cycleDateParts.monthKey)}`
    : '/leaderboards/monthly';
  const monthLeaderboardLabel = formatMonthLeaderboardLabel(cycleDateParts.monthKey);
  const publishedCycleHistory = useMemo(
    () => cycleHistory.filter((item) => Number(item?.submissionCount || 0) > 0),
    [cycleHistory]
  );
  const cycleHistoryDesc = useMemo(
    () => [...publishedCycleHistory].sort((a, b) => compareCycleIdsAsc(b?.cycleId, a?.cycleId)),
    [publishedCycleHistory]
  );
  const currentCycleMeta = useMemo(
    () => cycleHistoryDesc.find((item) => item?.cycleId === cycleId) || null,
    [cycleHistoryDesc, cycleId]
  );
  const cycleHistoryAsc = useMemo(
    () => [...publishedCycleHistory].sort((a, b) => compareCycleIdsAsc(a?.cycleId, b?.cycleId)),
    [publishedCycleHistory]
  );
  const currentCycleOrdinal = useMemo(
    () => cycleHistoryAsc.findIndex((item) => item?.cycleId === cycleId) + 1,
    [cycleHistoryAsc, cycleId]
  );
  const currentCycleDescIndex = useMemo(
    () => cycleHistoryDesc.findIndex((item) => item?.cycleId === cycleId),
    [cycleHistoryDesc, cycleId]
  );
  const newerCycle = currentCycleDescIndex > 0 ? cycleHistoryDesc[currentCycleDescIndex - 1] : null;
  const olderCycle = currentCycleDescIndex >= 0 ? cycleHistoryDesc[currentCycleDescIndex + 1] || null : null;
  const cycleRoman = toRomanNumeral(currentCycleOrdinal);
  const medalWeights = useMemo(() => getDailyMedalWeights(cycleId), [cycleId]);
  const topRows = useMemo(() => rows.filter((row) => Number(row?.rank) === 1), [rows]);
  const activeTopWinner = topRows[activeTopWinnerIndex] || topRows[0] || null;
  const winnerNames = Array.from(new Set(
    [
      ...payloadChampions.map((champion) => String(champion?.username || '').trim()),
      ...((Array.isArray(currentCycleMeta?.champions) ? currentCycleMeta.champions : []).map((champion) => String(champion?.username || '').trim())),
      ...topRows.map((row) => String(row?.profile?.username || row?.username || '').trim()),
      String(payloadWinnerPreview?.username || '').trim(),
      String(currentCycleMeta?.winnerPreview?.username || '').trim(),
    ].filter(Boolean)
  ));
  const activeWinnerName = String(
    activeTopWinner?.profile?.username
    || activeTopWinner?.username
    || ''
  ).trim();
  const winnerName = activeWinnerName || winnerNames.join(' & ');
  const winningAnswer = String(
    activeTopWinner?.answerText
      || payloadWinnerPreview?.answerText
      || currentCycleMeta?.winnerPreview?.answerText
      || topRows[0]?.answerText
      || ''
  ).trim();

  useEffect(() => {
    const winnerKey = `${cycleId}:${topRows.map((row) => [
      String(row?.uid || '').trim(),
      String(row?.answerText || '').trim(),
    ].join(':')).join('|')}`;
    let resetTimeoutId = null;
    if (topRows.length <= 1) {
      topWinnerRotationKeyRef.current = winnerKey;
      if (activeTopWinnerIndex !== 0) {
        resetTimeoutId = window.setTimeout(() => {
          setActiveTopWinnerIndex(0);
        }, 0);
      }
      return () => {
        if (resetTimeoutId != null) window.clearTimeout(resetTimeoutId);
      };
    }
    if (topWinnerRotationKeyRef.current !== winnerKey) {
      topWinnerRotationKeyRef.current = winnerKey;
      resetTimeoutId = window.setTimeout(() => {
        setActiveTopWinnerIndex(0);
      }, 0);
    }
    return () => {
      if (resetTimeoutId != null) window.clearTimeout(resetTimeoutId);
    };
  }, [activeTopWinnerIndex, cycleId, topRows]);

  useEffect(() => {
    if (typeof window === 'undefined' || topRows.length <= 1) return undefined;
    let fadeTimeoutId = null;
    const intervalId = window.setInterval(() => {
      setIsTopWinnerFading(true);
      fadeTimeoutId = window.setTimeout(() => {
        setActiveTopWinnerIndex((current) => (current + 1) % topRows.length);
        setIsTopWinnerFading(false);
      }, JOINT_WINNER_FADE_DURATION_MS);
    }, JOINT_WINNER_ROTATION_INTERVAL_MS + JOINT_WINNER_FADE_DURATION_MS);
    return () => {
      window.clearInterval(intervalId);
      if (fadeTimeoutId != null) window.clearTimeout(fadeTimeoutId);
    };
  }, [topRows.length]);

  const goToPage = useCallback((nextPage) => {
    const safe = Math.max(1, Math.min(totalPages, nextPage));
    const params = new URLSearchParams(window.location.search);
    params.set('cycleId', cycleId);
    params.set('page', String(safe));
    window.history.replaceState({}, '', `/daily/results?${params.toString()}`);
    setPage(safe);
  }, [cycleId, totalPages]);

  const navigateToCycle = useCallback((nextCycleId) => {
    const resolvedCycleId = String(nextCycleId || '').trim();
    if (!resolvedCycleId || resolvedCycleId === cycleId) return;
    const params = new URLSearchParams(window.location.search);
    params.set('cycleId', resolvedCycleId);
    params.set('page', '1');
    window.history.replaceState({}, '', `/daily/results?${params.toString()}`);
    setLoading(true);
    setError('');
    setPayload(null);
    setLocked(false);
    setLockFinalizing(false);
    setLockSeconds(null);
    setCopiedSubmissionId('');
    setActiveTopWinnerIndex(0);
    setIsTopWinnerFading(false);
    setCycleId(resolvedCycleId);
    setPage(1);
  }, [cycleId]);

  return (
    <>
    <main className="dailyPage">
      <div className="dailyPage__shell">
        <div className="dailyPage__topRow">
          <div className="dailyPage__title">Daily Acro Results</div>
          <Link href="/" className="dailyPage__closeLink" aria-label="Back to home" title="Back to home">
            <X size={16} strokeWidth={1.8} />
          </Link>
        </div>
        <div className="dailyPage__cycleNavWrap marketingFaqItem">
          <div className="dailyPage__cycleNav" aria-label="Cycle navigation">
            <button
              type="button"
              className="dailyPage__cycleNavBtn"
              onClick={() => navigateToCycle(olderCycle?.cycleId)}
              disabled={!olderCycle}
            >
              Prev
            </button>
            <div className="dailyPage__cycleNavDate">
              <span className="dailyPage__cycleNavLabel">
                {loading ? <span className="dailyPageSkeleton dailyPageSkeleton--navLabel" aria-hidden="true" /> : `Daily Acro ${cycleRoman || 'I'}`}
              </span>
              <span className="dailyPage__cycleNavSep" aria-hidden="true">·</span>
              <span>
                {loading ? (
                  <span className="dailyPageSkeleton dailyPageSkeleton--navDate" aria-hidden="true" />
                ) : cycleDateParts.weekday && cycleDateParts.day && cycleDateParts.month ? (
                  <>
                    <span>{cycleDateParts.weekday} {cycleDateParts.day} </span>
                    <Link className="dailyPage__cycleNavMonthLink" href={`/leaderboards/monthly?monthKey=${encodeURIComponent(cycleDateParts.monthKey)}`}>
                      {cycleDateParts.month}
                    </Link>
                  </>
                ) : (cycleDateLabel || 'Cycle')}
              </span>
              {!loading && !temporarilyHidden && winnerName ? (
                <>
                  <span className="dailyPage__cycleNavSep" aria-hidden="true">·</span>
                  <span
                    className="dailyPage__cycleNavWinner"
                    style={{
                      opacity: isTopWinnerFading ? 0 : 1,
                      transition: `opacity ${JOINT_WINNER_FADE_DURATION_MS}ms ease`,
                    }}
                  >
                    {topRows.length > 1 ? 'Joint Winner ' : 'Won By '}
                    <PlayerStatsTrigger uid={activeTopWinner?.uid} username={winnerName} profile={activeTopWinner}>{winnerName}</PlayerStatsTrigger>
                  </span>
                </>
              ) : null}
            </div>
            <button
              type="button"
              className="dailyPage__cycleNavBtn"
              onClick={() => navigateToCycle(newerCycle?.cycleId)}
              disabled={!newerCycle}
            >
              Next
            </button>
          </div>
        </div>
        {loading ? (
          <section className="dailyPage__promptRow">
            <div className="dailyPage__promptPanelGrid">
              <div className="dailyPage__promptFeature marketingFaqItem">
                <div className="dailyPage__paneBar dailyPage__paneBar--prompt">Prompt</div>
                <div className="dailyPage__paneBody dailyPage__paneBody--prompt">
                  <span className="dailyPageSkeleton dailyPageSkeleton--promptValue" aria-hidden="true" />
                </div>
              </div>
              <div className="dailyPage__lettersFeature marketingFaqItem">
                <div className="dailyPage__paneBar dailyPage__paneBar--letters">Letters</div>
                <div className="dailyPage__paneBody dailyPage__paneBody--letters">
                  <span className="dailyPageSkeleton dailyPageSkeleton--lettersValue" aria-hidden="true" />
                </div>
              </div>
            </div>
          </section>
        ) : temporarilyHidden ? (
          <section className="dailyPage__promptRow">
            <div className="dailyPage__promptPanelGrid">
              <div className="dailyPage__promptFeature marketingFaqItem">
                <div className="dailyPage__paneBar dailyPage__paneBar--prompt">Prompt</div>
                <div className="dailyPage__paneBody dailyPage__paneBody--prompt">
                  <div className="dailyPage__promptFeatureContent">
                    <strong className="dailyPage__promptFeatureValue">{hiddenMessage}</strong>
                  </div>
                </div>
              </div>
              <div className="dailyPage__lettersFeature marketingFaqItem">
                <div className="dailyPage__paneBar dailyPage__paneBar--letters">Letters</div>
                <div className="dailyPage__paneBody dailyPage__paneBody--letters">
                  <strong className="dailyPage__lettersValue dailyPage__lettersValue--hiddenMessage">{hiddenMessage}</strong>
                </div>
              </div>
            </div>
          </section>
        ) : cyclePrompt?.category || cyclePrompt?.letters ? (
          <section className="dailyPage__promptRow">
            <div className="dailyPage__promptPanelGrid">
              <div className="dailyPage__promptFeature marketingFaqItem">
                <div className="dailyPage__paneBar dailyPage__paneBar--prompt">Prompt</div>
                <div className="dailyPage__paneBody dailyPage__paneBody--prompt">
                  <div className="dailyPage__promptFeatureContent">
                    <strong className="dailyPage__promptFeatureValue">&ldquo;{promptCategory}&rdquo;</strong>
                    {winningAnswer ? (
                      <span
                        className="dailyPage__promptWinningAnswer"
                        style={{
                          opacity: isTopWinnerFading ? 0 : 1,
                          transition: `opacity ${JOINT_WINNER_FADE_DURATION_MS}ms ease`,
                        }}
                      >
                        &ldquo;{winningAnswer}&rdquo;
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              {promptLetters ? (
                <div className="dailyPage__lettersFeature marketingFaqItem" aria-label={`Prompt letters ${promptLetters.split('').join(' ')}`}>
                  <div className="dailyPage__paneBar dailyPage__paneBar--letters">Letters</div>
                  <div className="dailyPage__paneBody dailyPage__paneBody--letters">
                    <FitText
                      as="strong"
                      className="dailyPage__lettersValue"
                      compressor={(promptLetters.length || 5) * 0.1}
                      maxFontSize={80}
                      minFontSize={28}
                    >
                      <span className="dailyPage__lettersGlyph" data-text={promptLetters}>
                        <span className="dailyPage__lettersInset dailyPage__lettersInset--a" aria-hidden="true">{promptLetters}</span>
                        <span className="dailyPage__lettersInset dailyPage__lettersInset--b" aria-hidden="true">{promptLetters}</span>
                        <span className="dailyPage__lettersInset dailyPage__lettersInset--c" aria-hidden="true">{promptLetters}</span>
                        <span className="dailyPage__lettersInset dailyPage__lettersInset--d" aria-hidden="true">{promptLetters}</span>
                        <span className="dailyPage__lettersInset dailyPage__lettersInset--e" aria-hidden="true">{promptLetters}</span>
                        <span className="dailyPage__lettersFace">{promptLetters}</span>
                      </span>
                    </FitText>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {loading ? (
          <div className="dailyPage__card dailyPageLoadingCard">
            <div className="dailyPageLoadingCard__label">Loading results...</div>
            <span className="dailyPageSkeleton dailyPageSkeleton--tableRow" />
            <span className="dailyPageSkeleton dailyPageSkeleton--tableRow dailyPageSkeleton--tableRowWide" />
            <span className="dailyPageSkeleton dailyPageSkeleton--tableRow" />
          </div>
        ) : null}
        {error && !displayPayload ? <div className="dailyPage__card dailyPage__card--error">{error}</div> : null}

        {!loading && (!error || displayPayload) && locked ? (
          <>
            <DailyPhaseTimer phase="calculating" secondsToResultsReveal={lockSeconds} />
            <section className="dailyPage__card dailyPage__card--downtime">
              <p>Results are still being prepared. Today&apos;s entry and yesterday&apos;s voting are already live.</p>
              <div className="dailyPage__finalizing">
                {lockFinalizing ? 'Scoring, leaderboard updates, and email notices are still publishing.' : 'Results are currently calculating.'}
              </div>
              <div className="dailyPage__links">
                <Link className="dailyPage__linkBtn" href="/daily/enter">Enter Today&apos;s Daily</Link>
                <Link className="dailyPage__linkBtn" href="/daily/vote">Vote on Yesterday</Link>
              </div>
            </section>
          </>
        ) : null}

        {!loading && (!error || displayPayload) && !locked && temporarilyHidden && (
          <section className="dailyPage__card dailyPage__card--error">
            <p>{hiddenMessage}</p>
          </section>
        )}

        {!loading && (!error || displayPayload) && !locked && !temporarilyHidden && (
          <>
            <div className="dailyPage__tableWrap dailyPage__tableWrap--results marketingFaqItem">
              <table className="dailyPage__table dailyPage__table--results">
                <thead>
                  <tr>
                    <th className="dailyPage__rankHeader">Rank</th>
                    <th>Player</th>
                    <th>Answer</th>
                    <th>Likes</th>
                    <th>Score</th>
                    {!hideMonthlyColumn ? <th className="dailyPage__monthlyHeader">Monthly</th> : null}
                    <th>You</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const isViewer = Boolean(currentUserUid && row.uid && String(row.uid) === currentUserUid);
                    const isNewRankingRule = cycleId >= DAILY_RANKING_RULE_CHANGE_CYCLE_ID;
                    const isNonVoterRow = isNewRankingRule && !usesHalfScoreNonVoterRule && row.didVote === false;

                    const prevRow = rows[index - 1];
                    const showDidNotVoteDivider = 
                      isNewRankingRule &&
                      !usesHalfScoreNonVoterRule &&
                      prevRow && prevRow.didVote === true && row.didVote === false;
                    const hasHalfScoreAdjustment = usesHalfScoreNonVoterRule && row.didVote === false;

                    const numericRank = Number(row?.rank);
                    const username = String(row?.profile?.username || row?.username || '').trim() || 'Player';
                    let rankDisplay = String(row?.rank ?? '');
                    if (Number.isFinite(numericRank) && numericRank >= 1 && numericRank <= 3) {
                      const rankTone = numericRank === 1 ? 'gold' : (numericRank === 2 ? 'silver' : 'bronze');
                      const rankLabel = duplicateRankSet.has(numericRank) ? `Joint rank ${numericRank}` : `Rank ${numericRank}`;
                      rankDisplay = (
                        <span className={`dailyPage__rankMedal dailyPage__rankMedal--${rankTone}`} aria-label={rankLabel}>
                          <Trophy
                            size={20}
                            className={`medal-icon medal-icon--${rankTone}`}
                            style={{
                              color: rankTone === 'gold' ? '#fbbf24' : rankTone === 'silver' ? '#cbd5e1' : '#d97706',
                            }}
                          />
                        </span>
                      );
                    }
                    const goldVotes = Number(row?.goldVotes || 0);
                    const silverVotes = Number(row?.silverVotes || 0);
                    const bronzeVotes = Number(row?.bronzeVotes || 0);
                    const goldAwarderUsernames = normalizeAwarderUsernames(row?.goldAwarderUsernames);
                    const silverAwarderUsernames = normalizeAwarderUsernames(row?.silverAwarderUsernames);
                    const bronzeAwarderUsernames = normalizeAwarderUsernames(row?.bronzeAwarderUsernames);
                    const voteTotal = formatVoteTotal(row, cycleId);
                    const ratingSource = row?.ratingTier || displayPayload?.ratingTier || totalSubmissionCount;
                    const placementMonthlyPoints = Number.isFinite(Number(row?.baseMonthlyPoints))
                      ? Number(row.baseMonthlyPoints)
                      : Number.isFinite(Number(row?.monthlyPoints))
                        ? Math.max(0, Number(row.monthlyPoints) - Number(row?.votingBonusPoints || 0))
                        : getDailyPlacementMonthlyPoints(row?.percentileTier, ratingSource, row?.rank, totalSubmissionCount);
                    const viewerVoteTone = getViewerVoteTone(getRowSubmissionId(row), viewerVoteSelection);
                    const shouldShowVoteBonus = Boolean(viewerVoteTone) && Number(row?.rank) === 1;
                    const viewerVoteLabel = viewerVoteTone === 'gold'
                      ? 'Gold vote'
                      : viewerVoteTone === 'silver'
                        ? 'Silver vote'
                        : viewerVoteTone === 'bronze'
                          ? 'Bronze vote'
                          : 'No vote';
                    return (
                      <Fragment key={`${getRowSubmissionId(row) || row.uid || username}-${row.rank}`}>
                        {showDidNotVoteDivider && (
                          <tr className="dailyPage__dividerRow" data-row-type="divider">
                            <td colSpan={hideMonthlyColumn ? 6 : 7}>
                              <div className="dailyPage__dividerContent">
                                <span>Did not vote</span>
                                <HelpTooltipTrigger
                                  className="dailyPage__dividerHelpTip"
                                  iconClassName="dailyPage__dividerHelpIcon"
                                  icon={<HelpCircle size={13} strokeWidth={2.2} />}
                                  tooltipClassName="dailyPage__dividerTooltip"
                                  tooltipPosition="right"
                                >
                                  To encourage participation and keep the competition fair, voting players take priority in the rankings. Other entries are still ranked below by score and can still earn points.
                                </HelpTooltipTrigger>
                              </div>
                            </td>
                          </tr>
                        )}
                      <tr key={`${getRowSubmissionId(row) || row.uid || username}-${row.rank}`} className={`${isViewer ? 'dailyPage__row--me' : ''} ${isNonVoterRow ? 'dailyPage__row--nonVoter' : ''}`.trim()} data-row-type="entry">
                        <td className="dailyPage__rankCell">{rankDisplay}</td>
                        <td className="dailyPage__playerCell">
                          <span className="dailyPage__playerNameText"><PlayerStatsTrigger uid={row.uid} username={username} profile={row}>{username}</PlayerStatsTrigger></span>
                        </td>
                        <td className="dailyPage__answerCell">
                          <span className="dailyPage__answerInline">
                            <span className="dailyPage__answerInlineText">{row.answerText}</span>
                            {isViewer ? (
                              <button
                                type="button"
                                className={`inlineShareButton inlineShareButton--daily${copiedSubmissionId === getRowSubmissionId(row) ? ' inlineShareButton--copied' : ''}`}
                                title={copiedSubmissionId === getRowSubmissionId(row) ? 'Copied!' : 'Share your Daily result'}
                                onClick={() => handleShareResult(row)}
                              >
                                {copiedSubmissionId === getRowSubmissionId(row) ? (
                                  <Check size={11} strokeWidth={2.4} aria-hidden="true" />
                                ) : (
                                  <Copy size={10} strokeWidth={2} aria-hidden="true" />
                                )}
                                <span>{copiedSubmissionId === getRowSubmissionId(row) ? 'Copied' : 'SHARE'}</span>
                              </button>
                            ) : null}
                          </span>
                        </td>
                        <td><ResultLikeButton cycleId={cycleId} entryUid={row.uid} /></td>
                        <td className="dailyPage__pointsCell">
                          <div className="dailyPage__voteSummary">
                            {hasHalfScoreAdjustment ? (
                              <HelpTooltipTrigger
                                className="dailyPage__scoreAdjustmentTip"
                                iconClassName="dailyPage__voteTotal dailyPage__scoreAdjustmentTrigger"
                                icon={<>{voteTotal}<sup className="dailyPage__scoreAdjustmentMark" aria-hidden="true">*</sup></>}
                                ariaLabel={`${voteTotal}. Score halved because this player did not vote`}
                                tooltipClassName="dailyPage__dividerTooltip"
                                tooltipPosition="horizontal-auto"
                              >
                                To encourage participation and keep the competition fair, entries from players who do not vote have their final score halved. Their adjusted score determines their rank, and they can still win and earn points.
                              </HelpTooltipTrigger>
                            ) : (
                              <span className="dailyPage__voteTotal">{voteTotal}</span>
                            )}
                            <span className="dailyPage__voteBreakdown">
                              <span className="dailyPage__voteParen">(</span>
                              <HelpTooltipTrigger
                                className="dailyPage__voteMedalTip"
                                iconClassName="dailyPage__voteMedalTipTrigger dailyPage__voteMedal dailyPage__voteMedal--gold"
                                icon={goldVotes}
                                ariaLabel="Show gold vote awarders"
                                tooltipClassName="dailyPage__voteMedalTooltip dailyPage__voteMedalTooltip--gold"
                                tooltipPosition="auto"
                              >
                                <span className="dailyPage__voteMedalTooltipTitle dailyPage__voteMedalTooltipTitle--gold">Gold votes · {medalWeights.gold} pts each</span>
                                {goldAwarderUsernames.length > 0 ? (
                                  goldAwarderUsernames.map((awarder) => (
                                    <span key={`gold-${getRowSubmissionId(row)}-${awarder}`} className="dailyPage__voteMedalTooltipRow">
                                      {awarder}
                                    </span>
                                  ))
                                ) : (
                                  <span className="dailyPage__voteMedalTooltipRow dailyPage__voteMedalTooltipRow--empty">No gold votes</span>
                                )}
                              </HelpTooltipTrigger>
                              <HelpTooltipTrigger
                                className="dailyPage__voteMedalTip"
                                iconClassName="dailyPage__voteMedalTipTrigger dailyPage__voteMedal dailyPage__voteMedal--silver"
                                icon={silverVotes}
                                ariaLabel="Show silver vote awarders"
                                tooltipClassName="dailyPage__voteMedalTooltip dailyPage__voteMedalTooltip--silver"
                                tooltipPosition="auto"
                              >
                                <span className="dailyPage__voteMedalTooltipTitle dailyPage__voteMedalTooltipTitle--silver">Silver votes · {medalWeights.silver} pts each</span>
                                {silverAwarderUsernames.length > 0 ? (
                                  silverAwarderUsernames.map((awarder) => (
                                    <span key={`silver-${getRowSubmissionId(row)}-${awarder}`} className="dailyPage__voteMedalTooltipRow">
                                      {awarder}
                                    </span>
                                  ))
                                ) : (
                                  <span className="dailyPage__voteMedalTooltipRow dailyPage__voteMedalTooltipRow--empty">No silver votes</span>
                                )}
                              </HelpTooltipTrigger>
                              <HelpTooltipTrigger
                                className="dailyPage__voteMedalTip"
                                iconClassName="dailyPage__voteMedalTipTrigger dailyPage__voteMedal dailyPage__voteMedal--bronze"
                                icon={bronzeVotes}
                                ariaLabel="Show bronze vote awarders"
                                tooltipClassName="dailyPage__voteMedalTooltip dailyPage__voteMedalTooltip--bronze"
                                tooltipPosition="auto"
                              >
                                <span className="dailyPage__voteMedalTooltipTitle dailyPage__voteMedalTooltipTitle--bronze">Bronze votes · {medalWeights.bronze} pt each</span>
                                {bronzeAwarderUsernames.length > 0 ? (
                                  bronzeAwarderUsernames.map((awarder) => (
                                    <span key={`bronze-${getRowSubmissionId(row)}-${awarder}`} className="dailyPage__voteMedalTooltipRow">
                                      {awarder}
                                    </span>
                                  ))
                                ) : (
                                  <span className="dailyPage__voteMedalTooltipRow dailyPage__voteMedalTooltipRow--empty">No bronze votes</span>
                                )}
                              </HelpTooltipTrigger>
                              <span className="dailyPage__voteParen">)</span>
                            </span>
                          </div>
                        </td>
                        {!hideMonthlyColumn ? (
                          <td className="dailyPage__monthlyCell">
                            <div className="dailyPage__monthlyValue">
                              <span className="dailyPage__pointsValue">+{Number(placementMonthlyPoints || 0)}</span>{' '}
                              {cycleId < '2026-10-08' ? <span className="dailyPage__pointsMeta">({formatPercentileLabel(row.percentileTier)})</span> : null}
                            </div>
                          </td>
                        ) : null}
                        <td className="dailyPage__viewerVoteCell">
                          {viewerVoteTone ? (
                            <span className="dailyPage__viewerVoteWrap">
                              <span
                                className={`dailyPage__viewerVoteBadge dailyPage__viewerVoteBadge--${viewerVoteTone}`}
                                aria-label={viewerVoteLabel}
                                title={viewerVoteLabel}
                              >
                                <Award size={14} className={`medal-icon medal-icon--${viewerVoteTone}`} />
                              </span>
                              {shouldShowVoteBonus ? (
                                <span
                                  className="dailyPage__viewerVoteBonus"
                                  aria-label="Plus 2 monthly points"
                                >
                                  +2
                                </span>
                              ) : (
                                <span className="dailyPage__viewerVoteBonus dailyPage__viewerVoteBonus--ghost" aria-hidden="true">+2</span>
                              )}
                            </span>
                          ) : (
                            <span className="dailyPage__viewerVoteEmpty" aria-hidden="true" />
                          )}
                        </td>
                      </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 ? (
              <div className="dailyPage__pagination">
                <button type="button" className="dailyPage__actionBtn" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                  Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button type="button" className="dailyPage__actionBtn" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                  Next
                </button>
              </div>
            ) : null}
            {lowEntryFootnote ? (
              <p className="dailyPage__footnote">
                {lowEntryFootnote}
              </p>
            ) : null}
          </>
        )}
        <div className="dailyPage__bottomAction">
          <Link href="/" className="dailyPage__homeLink">
            <ArrowLeft size={16} strokeWidth={2} />
            <span>Back to Home</span>
          </Link>
          <Link href={monthLeaderboardHref} className="dailyPage__homeLink dailyPage__homeLink--secondary">
            <BarChart3 size={16} strokeWidth={2} />
            <span>{monthLeaderboardLabel}</span>
          </Link>
        </div>
        <SiteFooter />
      </div>
    </main>
    <style jsx global>{`
      .dailyPage__dividerRow {
        padding: 0;
        border: none;
      }
      /* Remove shared borders from adjacent rows to let the divider define the line */
      tr:has(+ .dailyPage__dividerRow) td {
        border-bottom: none !important;
      }
      .dailyPage__dividerRow + tr[data-row-type="entry"] td {
        border-top: none !important;
      }
      /* Ensure the divider row itself has no borders from the table cells */
      .dailyPage__dividerRow td {
        border-bottom: none;
        border-top: none;
        padding-left: 0;
        padding-right: 0;
      }
      .dailyPage__dividerContent {
        padding: 0.2rem 0; /* Adjusted padding to make it shorter */
        font-size: 0.58rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: rgba(148, 163, 184, 0.45);
        display: flex;
        align-items: center;
        gap: 0.35rem;
        white-space: nowrap;
       } 
      .dailyPage__dividerContent::before {
        content: "";
        width: 4.48rem; /* Start the line within the rank column */
        height: 1px;
        background: rgba(148, 163, 184, 0.12);
        border-radius: 99px;
        flex-shrink: 0;
      }
      .dailyPage__dividerContent::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(148, 163, 184, 0.12);
        border-radius: 99px;
        margin-right: 0;
      }
      .dailyPage__dividerHelpTip {
        position: relative;
      }
      .dailyPage__dividerHelpIcon {
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
        color: rgba(148, 163, 184, 0.45) !important;
        cursor: help;
        transition: color 0.2s ease;
      }
      .dailyPage__dividerHelpIcon:hover {
        color: rgba(148, 163, 184, 0.8) !important;
      }
      .dailyPage__dividerTooltip {
        position: absolute;
        z-index: 100;
        top: 50%;
        font-weight: normal; /* Make non-bold */
        left: calc(100% + 0.6rem);
        transform: translateY(-50%) translateX(-4px);
        width: max-content;
        padding: 0.5rem 0.75rem;
        background: rgba(15, 23, 42, 0.98);
        border: 1px solid rgba(148, 163, 184, 0.25);
        border-radius: 5px; /* Ensure 5px radius */
        color: rgba(226, 232, 240, 0.95);
        font-size: 0.7rem;
        line-height: 1.4;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s ease, transform 0.15s ease;
        text-transform: none;
        max-width: 320px; /* Wider box for the longer non-bold text */
        letter-spacing: normal;
        white-space: normal;
      }
      .dailyPage__dividerTooltip[data-tooltip-position="left"] {
        right: calc(100% + 0.6rem);
        left: auto;
        transform: translateY(-50%) translateX(4px);
      }
      .dailyPage__dividerHelpTip:hover .dailyPage__dividerTooltip,
      .dailyPage__dividerHelpTip[data-open="true"] .dailyPage__dividerTooltip,
      .dailyPage__scoreAdjustmentTip:hover .dailyPage__dividerTooltip,
      .dailyPage__scoreAdjustmentTip:focus-within .dailyPage__dividerTooltip,
      .dailyPage__scoreAdjustmentTip[data-open="true"] .dailyPage__dividerTooltip {
        opacity: 1;
        transform: translateY(-50%) translateX(0);
      }
      .dailyPage__row--nonVoter {
        opacity: 0.5;
      }
      .dailyPage__row--nonVoter .dailyPage__voteTotal,
      .dailyPage__row--nonVoter .dailyPage__pointsValue {
        font-style: italic;
      }
      .dailyPage__scoreAdjustmentMark {
        margin-left: 0.08em;
        font-size: 0.52em;
        line-height: 0;
        vertical-align: super;
      }
      .dailyPage__scoreAdjustmentTip {
        position: relative;
        display: inline-flex;
      }
      .dailyPage__scoreAdjustmentTrigger {
        appearance: none;
        padding: 0 0 0.06em;
        border: 0;
        border-bottom: 1px dotted rgba(248, 250, 252, 0.34);
        background: transparent;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        cursor: help;
      }
    `}</style>
    </>
  );
}
